import SparkMD5 from 'spark-md5'
import { createUploader } from './uploader'
import UserStore from '../stores/user-store'

// Dedicated instance so preview uploads don't pollute the main uploader's stats/done events. Cheap:
// uploadFile() spins up no worker iframes. Same config as the main uploader; token kept in sync.
var previewUploader = createUploader();

function syncPreviewToken() {
    // Only push a real token; updateToken(falsy) would break the anonymous-upload default.
    var token = UserStore.getData().userToken;
    if (token) {
        previewUploader.updateToken(token);
    }
}
UserStore.addChangeListener(syncPreviewToken);
syncPreviewToken();

// Read the file's bytes. Prefer Blob.arrayBuffer(); fall back to FileReader for older browsers.
function readArrayBuffer(file) {
    if (typeof file.arrayBuffer === 'function') {
        return file.arrayBuffer();
    }
    return new Promise(function (resolve, reject) {
        var reader = new FileReader();
        reader.onload = function () { resolve(reader.result); };
        reader.onerror = function () { reject(reader.error || new Error('Failed to read preview file')); };
        reader.readAsArrayBuffer(file);
    });
}

// Base64 of the raw 16-byte MD5 digest — the exact format StorageCore expects and the same output
// online-uploader's md5worker iframe produced (it hex-hashed then base64'd the bytes; raw hash + btoa
// is equivalent).
function computeBase64Md5(file) {
    return readArrayBuffer(file).then(function (arrayBuffer) {
        return btoa(SparkMD5.ArrayBuffer.hash(arrayBuffer, true));
    });
}

// Upload a product-preview JPEG WITHOUT Uploader.importFile. importFile hashes the file in an <iframe>
// worker whose global window.postMessage bus is shared with the page, so browser extensions (whose
// content scripts also inject into the iframe) post messages the worker can't parse and crash it
// ("Cannot read properties of undefined (reading 'url')"), leaving the upload hung. We hash
// synchronously here with spark-md5 (the same lib the iframe used) and drive the existing public
// uploadFile(), which still performs all the StorageCore register/upload/endUpload calls. Previews are
// disposable, so importFile's validation/dimensions/dedup steps are not needed.
//
// We resolve on the uploadComplete event, NOT the uploadFile() promise: uploadFile() only settles once
// StorageCore returns thumbnail metadata, but preview types never get thumbnails, so its promise hangs
// forever (online-uploader's _isStorageCoreDataValid only whitelists the non-anonymous
// "ProductPreviewImage"; anonymous sessions must use "AnonymousProductPreviewImage" per StorageCore, so
// the thumbnail fallback can never pass). We don't need that metadata — only the regId, which is set at
// registration, and the bytes are in StorageCore once endUpload fires (right before uploadComplete).
export function uploadPreview(file) {
    file.storageFileType = 'AnonymousProductPreviewImage';

    return computeBase64Md5(file).then(function (base64md5Checksum) {
        // Fields importFile would normally set before uploadFile reads them. uploadFile only calls
        // throwIfCanceled() on the token, so a no-op token suffices (the real CancellationToken class
        // isn't exported from the package). localId keys the file in the shared upload queue.
        file.base64md5Checksum = base64md5Checksum;
        file.localId = 'preview-' + base64md5Checksum;
        file.cancelled = false;
        file.cancelTokenList = [{ name: 'fileImport', cancelToken: { throwIfCanceled: function () {} } }];

        return new Promise(function (resolve, reject) {
            function cleanup() {
                previewUploader.unsubscribeUploadCompleteEvent(onComplete);
                previewUploader.unsubscribeUploadErrorEvent(onError);
            }
            function onComplete(completedFile) {
                if (completedFile !== file) return;
                cleanup();
                resolve(file.regId);
            }
            function onError(erroredFile, error) {
                if (erroredFile !== file) return;
                cleanup();
                reject(error);
            }

            previewUploader.uploadCompleteEvent(onComplete);
            previewUploader.uploadErrorEvent(onError);

            // Drives register → upload → endUpload. Its promise never settles for previews (see above);
            // swallow it so it can't surface as an unhandled rejection. Failures still reach onError.
            previewUploader.uploadFile(file)['catch'](function () {});
        });
    });
}
